import {
  Button,
  Card,
  Grid,
  MenuItem,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import ErrorText from 'components/ErrorText/ErrorText';
import SnackbarContext from 'contexts/snackbarContext';
import { getImpactReportById} from 'services/impactReport.services';

import { OrganisationType } from 'types/organisations.type';
import { ImpactReportTemplateType } from 'types/impactReportTemplate.type';
import { ImpactReportModelType } from 'types/impactReportModel.type';
import { theme } from 'theme';
import DatePicker from 'react-date-picker';

import { OrganisationBaseType } from 'types/organisations.type';
import { addMonths, startOfMonth, endOfMonth, startOfDay } from 'date-fns';
/*
import { ImpactReportTemplateType } from 'types/impactReportTemplate.type';
import { ImpactReportModelType } from 'types/impactReportModel.type';
*/

type EditImpactReportFormType = {
  organisation: OrganisationBaseType;
  template: ImpactReportTemplateType;
  model: ImpactReportModelType;
  createdAt: Date;
  dateFrom: Date;
  dateTo: Date;
  documentPathsDocx: string[];
  documentPathsPDF: string[];
  emailSubject?: string;
  emailText?: string;
  recipients?: string;
  generationMode: 'merged' | 'individual';
  generationFrequency: 'monthly' | 'quaterly' | 'yearly';
};

const CustomForm = styled('form')`width: 100%;`;

interface EditImpactReportFormProps {
  impactReportId?: string;
  organisationList: OrganisationType[];
  impactReportTemplateList: ImpactReportTemplateType[];
  impactReportModelList: ImpactReportModelType[];

  emailSubject?: string;
  emailText?: string;
  recipients?: string;
  generationMode: 'merged' | 'individual';
  generationFrequency: 'monthly' | 'quaterly' | 'yearly';
}

export const getDownloadUrl = (rawPath: string): string => {
  if (!rawPath) return '';

  // 1) Kapcsos zárójelek le
  const withoutBraces = rawPath.replace(/[{}]/g, '').trim();

  // 2) Base URL beállítás + '/api' levágása
  const envBase = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  const base = envBase.replace(/\/api\/?$/, '').replace(/\/$/, '');

  // 3) Ha abszolút URL érkezik, csak a pathname-t használjuk
  let relativePath = withoutBraces;
  if (/^https?:\/\//i.test(withoutBraces)) {
    try {
      const u = new URL(withoutBraces);
      relativePath = u.pathname + (u.search || '');
    } catch {
      // ha valamiért invalid URL, marad az eredeti
    }
  }

  // 4) Az útvonal elejéről 'api/' levágása, ha lenne
  relativePath = relativePath.replace(/^\/?api\/?/, '');

  // 5) Ne legyen dupla perjel
  relativePath = relativePath.replace(/^\/+/, '');

  return `${base}/${relativePath}`;
};

export default function EditImpactReportForm({impactReportId} : EditImpactReportFormProps) {
  const { t } = useTranslation();
  const {
    handleSubmit, 
    formState: { errors }, 
    setValue, 
    control, 
    watch,
  } = useForm<EditImpactReportFormType>();
  const { handleOpen } = useContext(SnackbarContext);
  
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {

    if (id) {
      getImpactReportById(id).then((data) => {
        console.log(data);

        setValue('organisation', data.organisation);
        setValue('template', data.template);
        
        setValue('model', data.model);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        today.setMonth(today.getMonth() - 1);
        setValue('dateFrom', data.dateFrom ? new Date(data.dateFrom) : startOfMonth(today));
        setValue('dateTo', data.dateTo ? new Date(data.dateTo) : endOfMonth(today));
        setValue('createdAt', data.createdAt ? new Date(data.createdAt) : new Date());
        setValue(
          'documentPathsDocx',
          (data.documentPathDocx || []).map(
            (path: string) => process.env.REACT_APP_API_BASE_URL + '/../' + path
          )
        );
        setValue(
          'documentPathsPDF',
          (data.documentPathPDF || []).map(
            (path: string) => process.env.REACT_APP_API_BASE_URL + '/../' + path
          )
        );

        setValue('emailSubject', data.emailSubject);
        setValue('emailText', data.emailText);
        setValue('recipients', data.recipients?.join(', '));
        setValue('generationMode', data.generationMode);
        setValue('generationFrequency', data.generationFrequency);
      });
    }
  }, [id]);


  const checkKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  return (
    <Card sx={{ padding: '40px', maxWidth: '800px' }}>
      <CustomForm onKeyDown={checkKeyDown}>
        <Grid container spacing={2}>


          {/* Date fields */}
          <Grid item xs={4}>
            <Typography style={{ fontSize: 12, color: theme.palette.grey[600] }}>{t('formFields.dateFrom')}</Typography>
            <Controller
              control={control}
              name="dateFrom"
              defaultValue={new Date()}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  value={value}
                  disabled = {true}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography style={{ fontSize: 12, color: theme.palette.grey[600] }}>{t('formFields.dateTo')}</Typography>
            <Controller
              control={control}
              name="dateTo"
              defaultValue={new Date()}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  value={value}
                  disabled = {true}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Typography style={{ fontSize: 12, color: theme.palette.grey[600] }}>{t('formFields.createdAt')}</Typography>
            <Controller
              control={control}
              name="createdAt"
              defaultValue={new Date()}
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  value={value}
                  disabled = {true}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Controller control={control} name="organisation" render={({ field: { value, onChange } }) => (<TextField disabled={true}  onChange={onChange} fullWidth variant="outlined" type="text" value={value?.companyName || ''} label={t('formFields.organisationDetails')} />)}/>
          </Grid>

          <Grid item xs={12}>
            <Controller control={control} name="template" render={({ field: { value, onChange } }) => (<TextField disabled={true}  onChange={onChange} fullWidth variant="outlined" type="text" value={value?.name || ''} label={t('formFields.templateDetails')} />)}/>
          </Grid>


          <Grid item xs={12}>
            <Controller control={control} name="model" render={({ field: { value, onChange } }) => (<TextField disabled={true}  onChange={onChange} fullWidth variant="outlined" type="text" value={value?.name || ''} label={t('formFields.modelDetails')} />)}/>
          </Grid>

          <Grid item xs={12}>
            <Controller control={control} name="emailSubject" render={({ field: { value } }) => (
              <TextField
                disabled
                fullWidth
                variant="outlined"
                label={t('formFields.emailSubject')}
                value={value || ''}
              />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Controller control={control} name="emailText" render={({ field: { value } }) => (
              <TextField
                disabled
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                label={t('formFields.emailText')}
                value={value || ''}
              />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Controller control={control} name="recipients" render={({ field: { value } }) => (
              <TextField
                disabled
                fullWidth
                variant="outlined"
                label={t('formFields.recipients')}
                value={value || ''}
              />
            )} />
          </Grid>

          <Grid item xs={12}>
            <Controller
              control={control}
              name="generationFrequency"
              defaultValue="monthly"
              render={({ field: { value } }) => (
                <TextField
                  select
                  disabled
                  fullWidth
                  label={t('formFields.generationFrequency')}
                  value={value}
                >
                  <MenuItem value="weekly">{t('generationFrequency.weekly')}</MenuItem>
                  <MenuItem value="monthly">{t('generationFrequency.monthly')}</MenuItem>
                  <MenuItem value="quaterly">{t('generationFrequency.quaterly')}</MenuItem>
                  <MenuItem value="yearly">{t('generationFrequency.yearly')}</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              control={control}
              name="generationMode"
              defaultValue="merged"
              render={({ field: { value } }) => (
                <TextField
                  select
                  disabled
                  fullWidth
                  label={t('formFields.generationMode')}
                  value={value}
                >
                  <MenuItem value="merged">{t('generationMode.merged')}</MenuItem>
                  <MenuItem value="individual">{t('generationMode.individual')}</MenuItem>
                </TextField>
              )}
            />
          </Grid>

        </Grid>

        {/* DOCX fájlok gombjai */}
        <Controller
          name="documentPathsDocx"
          control={control}
          defaultValue={[]}
          render={({ field: { value } }) => (
            <Grid container spacing={2} sx={{ marginTop: 3 }}>
              {value.map((path, index) => (
                <Grid item key={`docx-${index}`}>
                  <a href={getDownloadUrl(path)} target="_blank" rel="noopener noreferrer">
                    <Button variant="contained" color="primary">
                      {t('formFields.downloadButtonLabel', { extension: 'DOCX', index: index + 1 })}
                    </Button>
                  </a>
                </Grid>
              ))}
            </Grid>
          )}
        />

        {/* PDF fájlok gombjai */}
        <Controller
          name="documentPathsPDF"
          control={control}
          defaultValue={[]}
          render={({ field: { value } }) => (
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              {value.map((path, index) => (
                <Grid item key={`pdf-${index}`}>
                  <a href={getDownloadUrl(path)} target="_blank" rel="noopener noreferrer">
                    <Button variant="contained" color="secondary">
                      {t('formFields.downloadButtonLabel', { extension: 'PDF', index: index + 1 })}
                    </Button>
                  </a>
                </Grid>
              ))}
            </Grid>
          )}
        />



      </CustomForm>
    </Card>
  );
}
